import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import { CodeInput } from "@/components/code-input/CodeInput.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useEmployeeTwoFactorSetup,
  useEmployeeTwoFactorSetupVerify,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { z } from "zod";

interface TwoFactorSetupFormProps {
  accessToken: string;
  onSuccess: (newAccessToken: string) => void;
  onSkip: () => void;
}

export function TwoFactorSetupForm({ onSkip, onSuccess }: TwoFactorSetupFormProps) {
  const { t } = useTranslation();
  const twoFactorSetup = useEmployeeTwoFactorSetup();
  const twoFactorSetupVerify = useEmployeeTwoFactorSetupVerify();

  const setupSchema = z.object({
    code: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
  });

  type SetupSchema = z.infer<typeof setupSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SetupSchema>({
    mode: "onSubmit",
    resolver: zodResolver(setupSchema),
  });

  useEffectOnce(() => {
    void twoFactorSetup.mutateAsync();
  });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const result = await twoFactorSetupVerify.mutateAsync({
        code: formData.code,
      });

      if (result?.access_token) {
        analyticsService.trackEvent(analyticEvents.twoFactorAuthentication.enabled);
        onSuccess(result?.access_token);
      }

      onSkip();
    } catch {
      // Errors are handled by the mutation hook
    }
  });

  return (
    <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
      <Stack gap={"2xl"}>
        <Stack gap={"lg"}>
          <Title
            header={t("core:two_factor_setup")}
            size={"lg"}
            caption={t("core:two_factor_setup_warning_logout_20_min")}
          />

          <Stack gap={"md"}>
            <ListItem
              preTitle={t("core:code_for_connection")}
              title={twoFactorSetup.data?.secret}
              titleSlot={twoFactorSetup.isPending ? <Skeleton className={"w-32 h-5"} /> : undefined}
              valueSlot={
                twoFactorSetup.data && (
                  <ButtonIcon
                    variant={"plain"}
                    icon={<FontAwesomeIcon icon={"fa-copy fa-light"} />}
                    onClick={() => {
                      if (twoFactorSetup.data?.secret) {
                        copyTextWithNotify(twoFactorSetup.data.secret);
                      }
                    }}
                  />
                )
              }
            />
            <Stack items={"center"} justify={"center"}>
              {twoFactorSetup.isPending ? (
                <Skeleton className={"aspect-square w-64 block"} />
              ) : (
                <img
                  src={twoFactorSetup.data?.qr_code_base64}
                  className={"aspect-square w-64 block"}
                  alt={""}
                />
              )}
            </Stack>
            <ListItem
              preTitle={t("core:codes_for_backup")}
              title={twoFactorSetup?.data?.backup_codes.join(",")}
              titleSlot={twoFactorSetup.isPending ? <Skeleton className={"w-32 h-5"} /> : undefined}
              valueSlot={
                <ButtonIcon
                  variant={"plain"}
                  icon={<FontAwesomeIcon icon={"fa-copy fa-light"} />}
                  onClick={() => {
                    if (twoFactorSetup?.data?.backup_codes.join(",")) {
                      copyTextWithNotify(twoFactorSetup.data?.backup_codes.join(","));
                    }
                  }}
                />
              }
            />
          </Stack>

          <form onSubmit={onSubmit}>
            <Stack gap={"lg"}>
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <CodeInput
                    label={t("common:code")}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={errors.code?.message}
                    onComplete={() => onSubmit()}
                  />
                )}
              />

              <ServerErrorField errors={errors} />

              <Stack gap={"md"}>
                <Button
                  variant={"secondary"}
                  type={"submit"}
                  size="lg"
                  className="w-full"
                  isLoading={twoFactorSetupVerify.isPending}
                  disabled={twoFactorSetupVerify.isPending || twoFactorSetup.isPending}
                >
                  {t("common:enable")}
                </Button>

                <Button
                  variant={"plain"}
                  type={"button"}
                  className="w-full"
                  onClick={onSkip}
                  disabled={twoFactorSetupVerify.isPending}
                >
                  {t("common:skip")}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </Stack>
    </Box>
  );
}
